import { useState, useEffect } from 'react';
import MonitorIcon from '@mui/icons-material/Monitor';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { ThreeDots } from 'react-loader-spinner';
import Scrollbar from '../../../components/scrollbar';
import { AssetTransfersTableRow, TableEmptyRows, TableNoData, UserTableHead } from '../../../sections/@dashboard/user';
import { Page } from '../../../components';
import { fNumber } from '../../../utils/formatNumber';
import { getAssetTransfers, exportAssetTransfers } from '../store/assetActions';
import { getComparator } from '../../../hooks/useTable';
import { applyFilter, emptyRows } from '../../../sections/@dashboard/user/utils';

const STATUS_OPTIONS = [
  { label: 'All Statuses', value: '' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Accepted', value: 'ACCEPTED' },
  { label: 'Rejected', value: 'REJECTED' },
];

export default function AssetTransfers() {
  const dispatch = useDispatch();
  const { isLoading, assetTransfers, count } = useSelector((state) => state.assetReducer);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isExporting, setIsExporting] = useState(false);

  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');
  const [filterName] = useState('');

  const [filters, setFilters] = useState({ status: '', startDate: '', endDate: '' });

  const fetchData = (pg = page, rpp = rowsPerPage, f = filters) => {
    dispatch(getAssetTransfers({ page: pg + 1, rowsPerPage: rpp, ...f }));
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const handleApplyFilters = () => {
    setPage(0);
    fetchData(0, rowsPerPage, filters);
  };

  const handleResetFilters = () => {
    const reset = { status: '', startDate: '', endDate: '' };
    setFilters(reset);
    setPage(0);
    fetchData(0, rowsPerPage, reset);
  };

  const handleExport = async () => {
    setIsExporting(true);
    await dispatch(exportAssetTransfers(filters));
    setIsExporting(false);
  };

  const handleSort = (event, id) => {
    const isAsc = orderBy === id && order === 'asc';
    if (id !== '') {
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(id);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelected(assetTransfers?.map((n) => n.id));
      return;
    }
    setSelected([]);
  };

  const handleSelectClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) newSelected = newSelected.concat(selected, id);
    else if (selectedIndex === 0) newSelected = newSelected.concat(selected?.slice(1));
    else if (selectedIndex === selected.length - 1) newSelected = newSelected.concat(selected?.slice(0, -1));
    else if (selectedIndex > 0)
      newSelected = newSelected.concat(selected?.slice(0, selectedIndex), selected?.slice(selectedIndex + 1));
    setSelected(newSelected);
  };

  const dataFiltered = applyFilter({ inputData: assetTransfers, comparator: getComparator(order, orderBy), filterName });
  const notFound = !dataFiltered?.length && !!filterName;

  return (
    <Page title="Asset Transfers">
      <Container maxWidth="xl">
        <Grid container spacing={3}>
          <Grid item xs={12} md={4} lg={4}>
            <Paper elevation={3} sx={{ py: 2, textAlign: 'center' }}>
              <Box sx={{ mb: 0.5 }}>
                <MonitorIcon />
              </Box>
              <Typography variant="h6">{fNumber(count || 0)}</Typography>
              <Typography variant="body2">Transfers</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            {/* Filters */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" flexWrap="wrap">
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={filters.status}
                      onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="From Date"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={filters.startDate}
                    onChange={(e) => setFilters((p) => ({ ...p, startDate: e.target.value }))}
                    sx={{ minWidth: 160 }}
                  />
                  <TextField
                    label="To Date"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={filters.endDate}
                    onChange={(e) => setFilters((p) => ({ ...p, endDate: e.target.value }))}
                    sx={{ minWidth: 160 }}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button variant="contained" onClick={handleApplyFilters}>
                      Apply
                    </Button>
                    <Button variant="outlined" onClick={handleResetFilters}>
                      Reset
                    </Button>
                  </Stack>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<FileDownloadOutlinedIcon />}
                    onClick={handleExport}
                    disabled={isExporting}
                  >
                    {isExporting ? 'Exporting…' : 'Export CSV'}
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            {/* Table */}
            <Card>
              <CardHeader title="Asset Transfers" />
              <CardContent>
                <Scrollbar>
                  <TableContainer sx={{ minWidth: 800 }} component={Paper}>
                    {isLoading ? (
                      <Grid container sx={{ justifyContent: 'center' }}>
                        <ThreeDots height="80" width="80" radius="9" color="#2065d1" ariaLabel="three-dots-loading" visible />
                      </Grid>
                    ) : (
                      <Table size="small">
                        <UserTableHead
                          order={order}
                          orderBy={orderBy}
                          rowCount={assetTransfers?.length}
                          numSelected={selected.length}
                          onRequestSort={handleSort}
                          onSelectAllClick={handleSelectAllClick}
                          headLabel={[
                            { id: 'date', label: 'Date', alignRight: false },
                            { id: 'department', label: 'Department', alignRight: false },
                            { id: 'fromCustodian', label: 'From', alignRight: false },
                            { id: 'toCustodian', label: 'To', alignRight: false },
                            { id: 'totalAssets', label: '#Assets', alignRight: false },
                            { id: 'adminApproved', label: 'Admin Approved', alignRight: false },
                            { id: 'custodianApproved', label: 'Custodian Accepted', alignRight: false },
                            { id: 'status', label: 'Status', alignRight: false },
                            { id: '' },
                          ]}
                        />
                        <TableBody>
                          {dataFiltered?.map((row) => (
                            <AssetTransfersTableRow
                              key={row.id}
                              record={row}
                              selected={selected.indexOf(row.id) !== -1}
                              handleSelectClick={(event) => handleSelectClick(event, row.id)}
                              onTransferUpdate={fetchData}
                            />
                          ))}
                          <TableEmptyRows height={77} emptyRows={emptyRows(page, rowsPerPage, assetTransfers?.length)} />
                          {notFound && <TableNoData query={filterName} />}
                        </TableBody>
                      </Table>
                    )}
                  </TableContainer>
                </Scrollbar>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  component="div"
                  count={count || 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
